import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import classnames from 'classnames';
import { motion } from 'framer-motion';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';

import { Typo } from 'uikit/content';

import styles from '../styles/generatingToast.module.scss';

const GeneratingToast = ({
  status,
  className = '',
  defaultGenerateToastText = i18n.t('generating-content'),
  testId = '',
}) => {
  const generateToastVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
    },
    finished: {
      opacity: [1, 1, 0],
      transition: {
        times: [0, 0.96, 1],
        duration: 5,
      },
    },
  };

  let generateToastAnimation;
  let generateToastInitialState = 'hidden';
  let generateToastText = defaultGenerateToastText;
  if (status === 'in-progress') {
    generateToastInitialState = 'visible';
    generateToastAnimation = 'visible';
  } else if (status === 'success') {
    generateToastAnimation = 'finished';
    generateToastText = i18n.t('generation-complete');
  } else if (status === 'failure') {
    generateToastAnimation = 'finished';
    generateToastText = i18n.t('generation-failed');
  }

  const renderGenerateToastIcon = () => {
    if (status === 'in-progress') {
      return <AutoAwesomeOutlined className={styles.generateToastIcon} />;
    }

    if (status === 'success') {
      return (
        <CheckCircle
          className={classnames(styles.generateToastIcon, styles.generateToastIconSuccess)}
        />
      );
    }

    if (status === 'failure') {
      return (
        <RemoveCircleOutline
          className={classnames(styles.generateToastIcon, styles.generateToastIconFailure)}
        />
      );
    }

    return null;
  };

  return (
    <motion.div
      className={classnames(styles.generateToastBackdrop, className)}
      initial={generateToastInitialState}
      variants={generateToastVariants}
      animate={generateToastAnimation}
      layout="position"
      data-testid={testId}
    >
      <div
        className={classnames(styles.generateToast, {
          [styles.generateToastSuccess]: status === 'success',
          [styles.generateToastFailure]: status === 'failure',
        })}
      >
        {renderGenerateToastIcon()}
        <Typo element="span" variant="s03">
          {generateToastText}
        </Typo>
        {status === 'in-progress' && <div className={styles.dots} />}
      </div>
    </motion.div>
  );
};

GeneratingToast.propTypes = {
  className: PropTypes.string,
  status: PropTypes.string.isRequired,
  defaultGenerateToastText: PropTypes.string,
  testId: PropTypes.string,
};

export default React.memo(GeneratingToast);
